/**
 * The project accepts applicants only from some places, or not through a VPN
 * (backend `ip_access`), and this request was refused. Its own screen because
 * "Something went wrong" would send the applicant to retry something that will
 * fail the same way every time — and a VPN user to support instead of to their
 * VPN switch.
 *
 * The place shown is the server's reading of the applicant's own connection: it
 * is what lets someone tell "I am abroad" from "my VPN is on".
 */
import { useTranslation } from 'react-i18next';

import { StatusGlyph } from '../ui/StatusGlyph';
import type { AccessRefusal } from './accessRefusal';

/** "Region, Country" in the UI language; the region is the database's English name. */
function placeName(refusal: Extract<AccessRefusal, { kind: 'location' }>, language: string) {
  let country = refusal.country;
  if (refusal.countryCode) {
    try {
      country =
        new Intl.DisplayNames([language], { type: 'region' }).of(refusal.countryCode) ?? country;
    } catch {
      /* an old browser keeps the server's English name */
    }
  }
  return [refusal.region, country].filter(Boolean).join(', ');
}

export function AccessRefusedScreen({
  refusal,
  onRetry,
}: {
  refusal: AccessRefusal;
  onRetry: () => void;
}) {
  const { t, i18n } = useTranslation();

  if (refusal.kind === 'vpn') {
    return (
      <main className="screen">
        <div className="card enter" data-testid="access-refused" data-reason="vpn">
          <StatusGlyph kind="error" />
          <h1 className="step-title">
            {t('flow.access.vpn.heading', { defaultValue: 'Turn off your VPN' })}
          </h1>
          <p className="step-text" role="alert">
            {t('flow.access.vpn.body', {
              defaultValue:
                'This verification can’t be completed through a VPN or proxy. Turn it off, then try again.',
            })}
          </p>
          <button type="button" className="btn" data-track="retry" onClick={onRetry}>
            {t('flow.access.retry', { defaultValue: 'Try again' })}
          </button>
        </div>
      </main>
    );
  }

  const place = placeName(refusal, i18n?.language ?? 'en');
  return (
    <main className="screen">
      <div className="card enter" data-testid="access-refused" data-reason="location">
        <StatusGlyph kind="error" />
        <h1 className="step-title">
          {t('flow.access.location.heading', { defaultValue: 'Not available in your location' })}
        </h1>
        <p className="step-text" role="alert">
          {place
            ? t('flow.access.location.body', {
                defaultValue:
                  'Your connection is in {{place}}. This verification can’t be completed from there.',
                place,
              })
            : t('flow.access.location.bodyUnknown', {
                defaultValue: 'This verification can’t be completed from your location.',
              })}
        </p>
      </div>
    </main>
  );
}
